import CheckCircleIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import { Box, Button, Stack } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import DateRangeSelect from "@entrilia/shared/components/DateRangeSelect";
import SearchField from "@entrilia/shared/components/inputs/SearchField";
import MultiSelectFilter from "@entrilia/shared/components/MultiSelectFilter";
import useFetch from "@entrilia/shared/hooks/useFetch";
import CompaniesIcon from "@entrilia/shared/icons/CompaniesIcon";
import { logError } from "@entrilia/shared/logging";
import type { DescriptiveDateRange } from "@entrilia/shared/utilities/dateFilterHelper";
import { autoFormatCamelCase } from "@entrilia/shared/utilities/stringHelper";
import adminApi from "../../../../../api/adminApi";
import type { CashFlowPortalStatus, Investor } from "../../../../../api/types/fundStructureTypes";
import useClientContext from "../../../../../context/useClientContext";
import {
  cashFlowsFilterDateRanges,
  getInitialInvestorCashFlowsFilterState,
  searchCashFlows,
} from "./cashFlowsFiltering";
import InvestorCashFlowsTable from "./InvestorCashFlowsTable";

interface Props {
  investor: Investor;
}

const portalStatusOptions: CashFlowPortalStatus[] = ["None", "Preview", "Live"];

const InvestorCashFlowsTab = ({ investor }: Props) => {
  const { investorPortalApp } = useClientContext();

  const [filterState, setFilterState] = useState(getInitialInvestorCashFlowsFilterState);

  const getCashFlows = useCallback(
    () =>
      adminApi.getInvestorCashFlows(investor.id, {
        fundIds: filterState.selectedFundIds.length > 0 ? filterState.selectedFundIds : undefined,
        portalStatuses: filterState.selectedPortalStatuses.length > 0 ? filterState.selectedPortalStatuses : undefined,
        fromDueDate: filterState.selectedDateRange?.from?.toISOString(),
        toDueDate: filterState.selectedDateRange?.to?.toISOString(),
      }),
    [
      filterState.selectedDateRange?.from,
      filterState.selectedDateRange?.to,
      filterState.selectedFundIds,
      filterState.selectedPortalStatuses,
      investor.id,
    ]
  );

  const [cashFlows, fetchError, { isFetching }] = useFetch(getCashFlows);

  const filteredCashFlows = useMemo(
    () => searchCashFlows(cashFlows ?? [], filterState.searchTerm),
    [cashFlows, filterState.searchTerm]
  );

  const handleChangeSelectedPortalStatuses = (selectedPortalStatuses: string[]) => {
    setFilterState((prev) => ({
      ...prev,
      selectedPortalStatuses: selectedPortalStatuses as CashFlowPortalStatus[],
    }));
  };

  const handleChangeSelectedFunds = (selectedFundIds: string[]) => {
    setFilterState((prev) => ({
      ...prev,
      selectedFundIds,
    }));
  };

  const handleChangeDateRange = (selectedDateRange: DescriptiveDateRange) => {
    setFilterState((prev) => ({ ...prev, selectedDateRange }));
  };

  const handleSearch = (value: string) => {
    setFilterState((prev) => ({ ...prev, searchTerm: value.trim().toLowerCase() }));
  };

  const handleClearFilters = () => {
    setFilterState(getInitialInvestorCashFlowsFilterState());
  };

  if (fetchError) {
    logError(fetchError.message, "[InvestorCashFlowsTab] getCashFlows");
    return <DataLoadingFailed title="Loading investor cash flows failed" />;
  }

  const isLoading = cashFlows === undefined || isFetching;

  const isAnyFilterApplied =
    filterState.selectedPortalStatuses.length > 0 ||
    filterState.selectedFundIds.length > 0 ||
    filterState.selectedDateRange !== undefined ||
    filterState.searchTerm !== "";

  return (
    <Stack spacing={2.5} sx={{ height: "100%" }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
          <MultiSelectFilter
            disabled={isLoading || !investorPortalApp}
            placeholder="Portal Status"
            options={portalStatusOptions}
            getOptionValue={(s) => s}
            getOptionLabel={(s) => autoFormatCamelCase(s)}
            selectedValues={filterState.selectedPortalStatuses}
            onChange={handleChangeSelectedPortalStatuses}
            StartIcon={CheckCircleIcon}
          />

          <MultiSelectFilter
            disabled={isLoading}
            sortOptions
            placeholder="Funds"
            searchable
            options={investor.funds}
            getOptionValue={(f) => f.fundId}
            getOptionLabel={(f) => f.fundName}
            selectedValues={filterState.selectedFundIds}
            onChange={handleChangeSelectedFunds}
            StartIcon={CompaniesIcon}
          />

          <DateRangeSelect
            disabled={isLoading}
            predefinedRanges={cashFlowsFilterDateRanges}
            dateRange={filterState.selectedDateRange}
            onChange={handleChangeDateRange}
          />

          {isAnyFilterApplied && (
            <Button variant="text" onClick={handleClearFilters}>
              Clear all
            </Button>
          )}
        </Stack>

        <SearchField
          disabled={isLoading}
          onSearch={handleSearch}
          initialValue={filterState.searchTerm}
          debounceTimeMs={300}
        />
      </Box>
      <InvestorCashFlowsTable isLoading={isLoading} cashFlows={filteredCashFlows} />
    </Stack>
  );
};

export default InvestorCashFlowsTab;
